import { type ReactNode } from "react";
import { motion } from "motion/react";
import { FirmLogo } from "@/components/firm-logo";
import { PairSleepWakeToggle, type PairSleepWakeState } from "@/components/pair-sleep-wake-toggle";
import { cn } from "@/lib/utils";
import { fmtMoney } from "@/lib/format";
import { useDashboardStore, type AccountLive } from "@/store/dashboard.store";
import type { PairListItem } from "@/lib/api/pairs";
import type { TerminalMarket } from "@/lib/market";

export type Side = "buy" | "sell";

export interface AccountSelection {
  accountId: string;
  side: Side;
  /** Per-account lot override. `null` means "use the global lots in
   *  the order panel". Lets the operator punch a different size into
   *  one leg without changing the other. */
  lotsOverride: number | null;
}

/**
 * Right-side rail. One row per leg of the pair with:
 *   - Include checkbox (participates in the next Execute click)
 *   - Segmented BUY/SELL toggle (what direction for THIS leg)
 *   - Live equity + open PnL
 *   - Per-leg lot override field
 *
 * The parent owns selection state and passes us the current Map —
 * we're a controlled component, no internal store. That way the
 * Order Panel's "Execute" can read the same selection object without
 * any React context gymnastics.
 */
export function AccountsRail({
  pair,
  accountsLive,
  selection,
  onChange,
  market = "fx-cfd",
}: {
  pair: PairListItem;
  accountsLive: Record<string, AccountLive> | null;
  selection: Map<string, AccountSelection>;
  onChange(next: Map<string, AccountSelection>): void;
  market?: TerminalMarket;
}): ReactNode {
  const isFutures = market === "futures";
  // Slot labels are positional, NOT directional — direction is decided
  // per-trade. The two slots are just "the two accounts in this pair".
  const legs = [
    { ...pair.longAccount, legLabel: "Account A" },
    { ...pair.shortAccount, legLabel: "Account B" },
  ];

  const toggleInclude = (accountId: string, defaultSide: Side) => {
    const next = new Map(selection);
    if (next.has(accountId)) next.delete(accountId);
    else
      next.set(accountId, {
        accountId,
        side: defaultSide,
        lotsOverride: null,
      });
    onChange(next);
  };

  const setSide = (accountId: string, side: Side) => {
    const cur = selection.get(accountId);
    if (!cur) return;
    const next = new Map(selection);
    next.set(accountId, { ...cur, side });
    onChange(next);
  };

  const setLots = (accountId: string, value: string) => {
    const cur = selection.get(accountId);
    if (!cur) return;
    const trimmed = value.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    const next = new Map(selection);
    next.set(accountId, {
      ...cur,
      lotsOverride: Number.isFinite(parsed) && (parsed as number) > 0 ? (parsed as number) : null,
    });
    onChange(next);
  };

  // Pair-level sleep state. Live updates come from `pair:sleep-state`
  // events; on initial mount before the first event we fall back to
  // the snapshot embedded in the GET /api/pairs response.
  const sleepLive = useDashboardStore((s) => s.pairs[pair.id]?.sleep ?? null);
  // Per-pair positions slice — the per-leg P&L below is derived from
  // these (sum positions whose accountId matches the leg). Not from
  // accountsLive[].accountAggregateOpenPnl, which is account-wide.
  const livePositions = useDashboardStore(
    (s) => s.pairs[pair.id]?.positions ?? null,
  );
  const accountPairSlug = pair.accountPairSlug;
  const pairSleep: PairSleepWakeState | null = sleepLive
    ? {
        mode: sleepLive.mode,
        manualState: sleepLive.manualState,
        currentState: sleepLive.currentState,
      }
    : pair.pairSleep
      ? {
          mode: pair.pairSleep.mode,
          manualState: pair.pairSleep.manualState,
          currentState: pair.pairSleep.currentState,
        }
      : null;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card/60">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          Accounts
        </span>
        <span className="font-mono text-[10px] text-muted-foreground">
          {selection.size}/{legs.length} picked
        </span>
      </div>
      {pairSleep && accountPairSlug && (
        <div className="flex items-center justify-between gap-2 border-b border-border/40 bg-muted/20 px-3 py-1.5">
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Pair Bot
          </span>
          <PairSleepWakeToggle
            accountPairSlug={accountPairSlug}
            state={pairSleep}
            size="sm"
          />
        </div>
      )}

      <div className="flex flex-col divide-y divide-border/40 overflow-y-auto">
        {legs.map((leg, legIndex) => {
          const live = accountsLive?.[leg.accountId];
          const sel = selection.get(leg.accountId);
          const isPicked = sel != null;
          // Default the two slots to opposite sides so a fresh manual
          // pick produces a hedge by default. Operator can flip on the
          // order panel. Slot index is purely positional — direction is
          // decided per-trade, so the default is just a starting point.
          const defaultSide: Side = !isFutures && legIndex === 1 ? "sell" : "buy";
          const pickedSide = sel?.side ?? defaultSide;
          // Per-leg P&L for this symbol pair only (sum of positions in
          // this pair's slice belonging to the leg). Reading
          // live?.accountAggregateOpenPnl here would bleed in P&L from
          // every other symbol pair the same account is hedging on.
          let pnl = 0;
          if (livePositions) {
            for (const p of Object.values(livePositions)) {
              if (p.accountId === leg.accountId) pnl += p.openPnl;
            }
          }

          return (
            <motion.div
              key={leg.accountId}
              layout
              className={cn(
                "flex flex-col gap-2 px-3 py-3 transition-colors",
                isPicked && "bg-accent/5",
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
                  {!isFutures && (
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={() => toggleInclude(leg.accountId, defaultSide)}
                      aria-label={`Include ${leg.firmName} in next execute`}
                      className="h-4 w-4 rounded border-border bg-background accent-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      style={{ touchAction: "manipulation" }}
                    />
                  )}
                  <FirmLogo
                    src={leg.firmLogo}
                    name={leg.firmName}
                    className="h-8 w-8 rounded-md border border-border object-contain p-1"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-foreground">
                      {leg.firmName}
                    </p>
                    <p className="font-mono text-[10px] text-muted-foreground">
                      {leg.legLabel} · {leg.accountId.slice(0, 6)}
                    </p>
                  </div>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-1.5 text-[10px]">
                <div className="flex flex-col rounded-md bg-muted/40 px-2 py-1">
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Equity
                  </span>
                  <span className="font-mono tabular-nums text-foreground">
                    {live?.equity != null ? fmtMoney(live.equity) : "—"}
                  </span>
                </div>
                <div className="flex flex-col rounded-md bg-muted/40 px-2 py-1">
                  <span className="uppercase tracking-wide text-muted-foreground">
                    Open P&L
                  </span>
                  <span
                    className={cn(
                      "font-mono tabular-nums",
                      pnl >= 0 ? "text-emerald-500" : "text-rose-500",
                    )}
                  >
                    {pnl >= 0 ? "+" : ""}
                    {fmtMoney(pnl)}
                  </span>
                </div>
              </div>

              {!isFutures && isPicked && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col gap-1.5"
                >
                  <div className="grid grid-cols-2 overflow-hidden rounded-md border border-border" role="radiogroup" aria-label={`${leg.firmName} side`}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={pickedSide === "buy"}
                      onClick={() => setSide(leg.accountId, "buy")}
                      className={cn(
                        "px-2 py-1 text-[11px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
                        pickedSide === "buy"
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "text-muted-foreground hover:bg-muted",
                      )}
                      style={{ touchAction: "manipulation" }}
                    >
                      BUY
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={pickedSide === "sell"}
                      onClick={() => setSide(leg.accountId, "sell")}
                      className={cn(
                        "px-2 py-1 text-[11px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring",
                        pickedSide === "sell"
                          ? "bg-rose-500/15 text-rose-400"
                          : "text-muted-foreground hover:bg-muted",
                      )}
                      style={{ touchAction: "manipulation" }}
                    >
                      SELL
                    </button>
                  </div>
                  <label className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Lots
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="default"
                      autoComplete="off"
                      spellCheck={false}
                      aria-label={`Per-leg lot override for ${leg.firmName}`}
                      value={
                        sel?.lotsOverride == null ? "" : String(sel.lotsOverride)
                      }
                      onChange={(e) => setLots(leg.accountId, e.target.value)}
                      className="h-7 w-full rounded border border-border bg-background px-2 text-right font-mono text-[11px] tabular-nums focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                    />
                  </label>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
